// /home/labdoo/zitech-nexus/frontend/src/pages/admin/AdminBlogPostForm.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogAPI } from '../../services/api';
import './AdminBlogManagement.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Save: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Cancel: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Spinner: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="abm-spinner-small">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
};

const AdminBlogPostForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    read_time: 5,
    is_published: false,
    image_url: '',
    meta_title: '',
    meta_description: ''
  });

  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchPost();
    } else {
      setFetching(false);
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await blogAPI.adminGetCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPost = async () => {
    try {
      const response = await blogAPI.adminGetPosts();
      const post = response.data.find((p: any) => p.id === parseInt(id!));
      if (post) {
        setFormData({
          title: post.title,
          excerpt: post.excerpt,
          content: post.content || '',
          category: post.category,
          read_time: post.read_time,
          is_published: post.is_published,
          image_url: post.image_url || '',
          meta_title: post.meta_title || '',
          meta_description: post.meta_description || ''
        });
      }
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('excerpt', formData.excerpt);
      submitData.append('content', formData.content);
      submitData.append('category', formData.category);
      submitData.append('read_time', formData.read_time.toString());
      submitData.append('is_published', formData.is_published.toString());
      if (formData.image_url) submitData.append('image_url', formData.image_url);
      if (formData.meta_title) submitData.append('meta_title', formData.meta_title);
      if (formData.meta_description) submitData.append('meta_description', formData.meta_description);

      if (id) {
        await blogAPI.adminUpdatePost(parseInt(id!), submitData);
      } else {
        await blogAPI.adminCreatePost(submitData);
      }
      navigate('/dashboard/admin/blog/posts');
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="abm-loading-container">
        <div className="abm-spinner">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="abm-spinner-svg">
            <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        </div>
        <p className="abm-loading-text">Loading post...</p>
      </div>
    );
  }

  return (
    <div className="abm-form-container">
      {/* Header */}
      <div className="abm-form-header">
        <div className="abm-form-header-left">
          <button className="abm-back-btn" onClick={() => navigate('/dashboard/admin/blog/posts')}>
            <Icons.Back />
            Back to Posts
          </button>
          <h1 className="abm-form-title">{id ? 'Edit Post' : 'Create New Post'}</h1>
        </div>
        <button className="abm-btn-secondary" onClick={() => navigate('/dashboard/admin/blog/posts')}>
          <Icons.Cancel />
          Cancel
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="abm-form">
        <div className="abm-form-grid">
          {/* Left Column */}
          <div className="abm-form-left">
            <div className="abm-form-card">
              <h3 className="abm-form-card-title">Post Content</h3>
              
              <div className="abm-form-group">
                <label className="abm-form-label">Title *</label>
                <input
                  type="text"
                  name="title"
                  className="abm-form-input"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter post title"
                />
              </div>

              <div className="abm-form-group">
                <label className="abm-form-label">Excerpt *</label>
                <textarea
                  name="excerpt"
                  className="abm-form-textarea"
                  required
                  rows={3}
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  placeholder="Brief summary of the post (max 500 characters)"
                  maxLength={500}
                />
                <span className="abm-form-hint">{formData.excerpt.length}/500 characters</span>
              </div>

              <div className="abm-form-group">
                <label className="abm-form-label">Content *</label>
                <textarea
                  name="content"
                  className="abm-form-textarea abm-form-content"
                  required
                  rows={15}
                  value={formData.content}
                  onChange={handleInputChange}
                  placeholder="Write your blog post content here..."
                />
                <span className="abm-form-hint">You can use HTML tags: &lt;p&gt;, &lt;h1&gt;-&lt;h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;em&gt;</span>
              </div>
            </div>

            {/* SEO Section */}
            <div className="abm-form-card">
              <h3 className="abm-form-card-title">SEO Settings</h3>
              
              <div className="abm-form-group">
                <label className="abm-form-label">Meta Title</label>
                <input
                  type="text"
                  name="meta_title"
                  className="abm-form-input"
                  value={formData.meta_title}
                  onChange={handleInputChange}
                  placeholder="SEO title (leave empty to use post title)"
                  maxLength={60}
                />
                <span className="abm-form-hint">Recommended: 50-60 characters</span>
              </div>

              <div className="abm-form-group">
                <label className="abm-form-label">Meta Description</label>
                <textarea
                  name="meta_description"
                  className="abm-form-textarea"
                  value={formData.meta_description}
                  onChange={handleInputChange}
                  placeholder="SEO description (max 160 characters)"
                  rows={2}
                  maxLength={160}
                />
                <span className="abm-form-hint">Recommended: 150-160 characters</span>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="abm-form-right">
            <div className="abm-form-card">
              <h3 className="abm-form-card-title">Post Settings</h3>
              
              <div className="abm-form-group">
                <label className="abm-form-label">Category *</label>
                <select
                  name="category"
                  className="abm-form-select"
                  required
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="abm-form-group">
                <label className="abm-form-label">Read Time (minutes)</label>
                <input
                  type="number"
                  name="read_time"
                  className="abm-form-input"
                  value={formData.read_time}
                  onChange={handleInputChange}
                  min="1"
                />
              </div>

              <div className="abm-form-group">
                <label className="abm-form-label">Feature Image URL</label>
                <input
                  type="url"
                  name="image_url"
                  className="abm-form-input"
                  value={formData.image_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                />
                <span className="abm-form-hint">Enter a URL for the featured image (optional)</span>
              </div>

              <div className="abm-form-group abm-form-checkbox">
                <label className="abm-checkbox-label">
                  <input
                    type="checkbox"
                    name="is_published"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  />
                  <span className="abm-checkbox-text">Published (visible to public)</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="abm-form-actions">
              <button 
                type="submit" 
                className="abm-btn-primary abm-btn-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Icons.Spinner />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icons.Save />
                    {id ? 'Update Post' : 'Create Post'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminBlogPostForm;